import { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Layout";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", product: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill name, email & message");
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "", product: "" });
    } catch {
      toast.error("Could not send enquiry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 pb-16 max-w-[1600px] mx-auto px-6 md:px-10">
      <Seo title="Contact & Bulk Enquiry — Indoforce" description="Get in touch for product enquiries, bulk orders and trade pricing on air compressors and car wash systems." canonical={`${window.location.origin}/contact`} />
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-[#E31E24] mb-4">Get in touch</p>
        <h1 className="font-display text-5xl md:text-7xl font-black uppercase tracking-tighter leading-none">Request a quote</h1>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-12 mt-14">
        <Reveal>
          <p className="text-white/60 leading-relaxed mb-10 max-w-md">
            Need bulk pricing, technical advice or a product not listed here? Send us your requirement and our team will respond with a tailored quote.
          </p>
          <div className="space-y-6">
            {[
              { icon: Phone, t: "Call / WhatsApp", d: "+91 90000 00000" },
              { icon: Mail, t: "Email", d: "sales@indoforce.in" },
              { icon: MapPin, t: "Showroom", d: "Industrial Area, India" },
            ].map((c, i) => (
              <div key={i} className="flex items-center gap-4 border border-white/10 p-5">
                <c.icon className="text-[#E31E24]" size={22} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{c.t}</p>
                  <p className="font-display font-bold">{c.d}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="border border-white/10 p-6 md:p-8 space-y-5" data-testid="enquiry-form">
            {[
              { k: "name", label: "Name", type: "text" },
              { k: "email", label: "Email", type: "email" },
              { k: "phone", label: "Phone", type: "tel" },
              { k: "product", label: "Product / Category (optional)", type: "text" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-2">{f.label}</label>
                <input
                  data-testid={`enquiry-${f.k}`}
                  type={f.type}
                  value={form[f.k]}
                  onChange={(e) => set(f.k, e.target.value)}
                  className="w-full bg-transparent border border-white/20 focus:border-[#E31E24] outline-none px-4 py-3 text-sm"
                />
              </div>
            ))}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-2">Message</label>
              <textarea
                data-testid="enquiry-message"
                rows={4}
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                className="w-full bg-transparent border border-white/20 focus:border-[#E31E24] outline-none px-4 py-3 text-sm resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="enquiry-submit"
              className="btn-fill w-full flex items-center justify-center gap-3 border border-[#E31E24] text-[#E31E24] hover:text-black px-8 py-4 uppercase text-sm font-bold tracking-widest disabled:opacity-50"
            >
              {loading ? "Sending..." : <>Send enquiry <Send size={16} /></>}
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
